import { type FAQItem, isEmptyFAQ, isFAQModified } from '@/types/faq'

/**
 * Extract accepted and proposed data from a target FAQ for AI generation context.
 * Used when regenerating or updating an existing FAQ.
 */
export function extractFAQContext(targetFAQ: FAQItem): {
  acceptedData?: string
  proposedData?: string
} {
  const result: { acceptedData?: string; proposedData?: string } = {}

  // If the FAQ has existing content, use it as accepted_data
  if (targetFAQ.question.trim() && targetFAQ.answer.trim()) {
    result.acceptedData = JSON.stringify(
      {
        question: targetFAQ.question,
        answer: targetFAQ.answer,
      },
      null,
      2,
    )
  }

  // If there's a pending suggestion, use it as proposed_data
  if (targetFAQ.pendingSuggestion) {
    result.proposedData = targetFAQ.pendingSuggestion
  }

  return result
}

/**
 * Build a formatted FAQ list string for AI generation context.
 * Returns a numbered list of all valid FAQs, excluding the specified FAQ ID.
 * Format: "1. question: X\n   answer: Y\n\n2. question: Z\n   answer: W"
 */
export function buildFAQListString(faqs: FAQItem[], excludeFAQId: string): string {
  const allFAQs = faqs
    .filter(faq => !faq.isDeleted && faq.id !== excludeFAQId)
    .filter(faq => {
      // Include FAQs that have content (either saved or pending suggestion)
      if (faq.pendingSuggestion) return true
      return faq.question.trim() && faq.answer.trim()
    })
    .map(faq => {
      // Use pending suggestion if available, otherwise use saved values
      if (faq.pendingSuggestion) {
        try {
          const parsed = JSON.parse(faq.pendingSuggestion) as { question: string; answer: string }
          return { question: parsed.question, answer: parsed.answer }
        } catch {
          return null
        }
      }
      return { question: faq.question, answer: faq.answer }
    })
    .filter((faq): faq is { question: string; answer: string } => faq !== null)

  // Format as numbered list
  return allFAQs.map((faq, index) => `${index + 1}. question: ${faq.question}\n   answer: ${faq.answer}`).join('\n\n')
}

/**
 * Categorizes FAQs for save operations into different buckets.
 * Used to separate FAQs that need different API operations (create/update/delete).
 */
export interface CategorizedFAQs {
  activeFAQs: FAQItem[]
  deletedFAQs: FAQItem[]
  newFAQs: FAQItem[]
  existingFAQs: FAQItem[]
  incompleteFAQs: FAQItem[]
  validNewFAQs: FAQItem[]
  validExistingFAQs: FAQItem[]
  emptyNewFAQs: FAQItem[]
}

export function categorizeFAQsForSaving(faqs: FAQItem[]): CategorizedFAQs {
  const activeFAQs = faqs.filter(faq => !faq.isDeleted)
  const deletedFAQs = faqs.filter(faq => faq.isDeleted && !faq.isNew)

  const newFAQs = activeFAQs.filter(faq => faq.isNew)
  const existingFAQs = activeFAQs.filter(faq => !faq.isNew)

  const incompleteFAQs = activeFAQs.filter(faq => {
    const hasQuestion = faq.question.trim().length > 0
    const hasAnswer = faq.answer.trim().length > 0
    return hasQuestion !== hasAnswer
  })

  const validNewFAQs = newFAQs.filter(faq => !isEmptyFAQ(faq))
  const validExistingFAQs = existingFAQs.filter(faq => !isEmptyFAQ(faq) && isFAQModified(faq))
  const emptyNewFAQs = newFAQs.filter(faq => isEmptyFAQ(faq))

  return {
    activeFAQs,
    deletedFAQs,
    newFAQs,
    existingFAQs,
    incompleteFAQs,
    validNewFAQs,
    validExistingFAQs,
    emptyNewFAQs,
  }
}

/**
 * Type guard to validate server FAQ response structure.
 */
export interface ServerFAQ {
  question: string
  answer: string
  faqId: string
}

export function isValidServerFAQ(value: unknown): value is ServerFAQ {
  return (
    typeof value === 'object' &&
    value !== null &&
    'question' in value &&
    'answer' in value &&
    'faqId' in value &&
    typeof (value as ServerFAQ).question === 'string' &&
    typeof (value as ServerFAQ).answer === 'string' &&
    typeof (value as ServerFAQ).faqId === 'string'
  )
}

/**
 * Transforms FAQ items to API payload format for creation.
 */
export function faqsToCreatePayload(faqs: FAQItem[]): Array<{ question: string; answer: string }> {
  return faqs.map(faq => ({
    question: faq.question.trim(),
    answer: faq.answer.trim(),
  }))
}

/**
 * Transforms FAQ items to API payload format for updates.
 */
export function faqsToUpdatePayload(faqs: FAQItem[]): Array<{ faq_id: string; question: string; answer: string }> {
  return faqs.map(faq => ({
    faq_id: faq.id,
    question: faq.question.trim(),
    answer: faq.answer.trim(),
  }))
}

/**
 * Reconciles local FAQ items with server-returned FAQs after creation.
 * Matches server FAQs to local FAQs by question/answer content and updates IDs.
 */
export function reconcileCreatedFAQs(localFAQs: FAQItem[], serverResponse: unknown): FAQItem[] {
  if (!serverResponse || typeof serverResponse !== 'object' || !('faqs' in serverResponse)) {
    return localFAQs
  }

  const serverFAQs = (serverResponse as { faqs: unknown }).faqs
  if (!Array.isArray(serverFAQs)) {
    return localFAQs
  }

  return localFAQs.map(faq => {
    if (!faq.isNew) return faq

    const serverFAQ = serverFAQs.find(
      (sf): sf is ServerFAQ =>
        isValidServerFAQ(sf) && sf.question === faq.question.trim() && sf.answer === faq.answer.trim(),
    )

    if (serverFAQ) {
      return {
        ...faq,
        id: serverFAQ.faqId,
        isNew: false,
        originalQuestion: faq.question,
        originalAnswer: faq.answer,
      }
    }

    return faq
  })
}

/**
 * Updates FAQs to mark them as saved after successful update operation.
 * Syncs originalQuestion and originalAnswer with current values.
 */
export function markFAQsAsUpdated(localFAQs: FAQItem[], updatedFAQIds: string[]): FAQItem[] {
  return localFAQs.map(faq =>
    updatedFAQIds.includes(faq.id)
      ? {
          ...faq,
          originalQuestion: faq.question,
          originalAnswer: faq.answer,
        }
      : faq,
  )
}
